import { z } from "zod";

/** Main-owned grants; null is reserved for verified Admin authority. */
export const MainLegalEntityAccessV1Schema = z.strictObject({
  version: z.literal(1), actorUserId: z.string().trim().min(1),
  legalEntityIds: z.array(z.string().trim().min(1)).nullable(),
});

export const MainDefaultLegalEntityV1Schema = z.discriminatedUnion("state", [
  z.strictObject({
    version: z.literal(1), state: z.literal("ready"),
    legalEntityId: z.string().trim().min(1).max(128),
    name: z.string().min(1), code: z.string().min(1),
    sourceRevision: z.string().min(1), authorityVersion: z.number().int().positive(),
  }),
  z.strictObject({ version: z.literal(1), state: z.literal("unconfigured") }),
  z.strictObject({ version: z.literal(1), state: z.literal("unavailable"), reasonCode: z.string().min(1) }),
  z.strictObject({ version: z.literal(1), state: z.literal("denied"), reasonCode: z.string().min(1) }),
]);
export type MainDefaultLegalEntityV1 = z.infer<typeof MainDefaultLegalEntityV1Schema>;

export const SetMainDefaultLegalEntityV1Schema = z.strictObject({
  legalEntityId: z.string().trim().min(1).max(128),
  expectedVersion: z.number().int().positive(),
  expectedDefaultRevision: z.string().min(1).nullable(),
  reason: z.string().trim().min(8).max(1000),
});
export type SetMainDefaultLegalEntityV1 = z.infer<typeof SetMainDefaultLegalEntityV1Schema>;
export const MainDefaultLegalEntityReceiptV1Schema = z.strictObject({
  operationId: z.string().min(1),
  current: MainDefaultLegalEntityV1Schema,
  replayed: z.boolean(),
});

export type LegalEntityResolution = {
  state: "ready"; legalEntityId: string; source: "record" | "explicit" | "default";
  defaultRevision?: string;
} | { state: "unconfigured" | "unavailable" | "denied"; reasonCode: string };

/** A missing required binding and a forbidden scope must never become a default. */
export function resolveLegalEntityContext(input: {
  recordLegalEntityId?: string | null;
  recordBindingRequired?: boolean;
  explicitLegalEntityId?: string | null;
  defaultEntity: MainDefaultLegalEntityV1;
  allowedLegalEntityIds: readonly string[] | null;
}): LegalEntityResolution {
  if (input.recordBindingRequired && !input.recordLegalEntityId)
    return { state: "unavailable", reasonCode: "LEGAL_ENTITY_BINDING_REQUIRED" };
  const id = input.recordLegalEntityId ?? input.explicitLegalEntityId;
  const source = input.recordLegalEntityId ? "record" : "explicit";
  if (id !== undefined && id !== null) {
    if (!id.trim() || id.trim() !== id)
      return { state: "unavailable", reasonCode: "LEGAL_ENTITY_SCOPE_INVALID" };
    if (input.allowedLegalEntityIds !== null && !input.allowedLegalEntityIds.includes(id))
      return { state: "denied", reasonCode: "CAPABILITY_NOT_GRANTED" };
    return { state: "ready", legalEntityId: id, source };
  }
  const value = input.defaultEntity;
  if (value.state !== "ready")
    return { state: value.state, reasonCode: value.state === "unconfigured" ? "DEFAULT_LEGAL_ENTITY_NOT_CONFIGURED" : value.reasonCode };
  if (input.allowedLegalEntityIds !== null && !input.allowedLegalEntityIds.includes(value.legalEntityId))
    return { state: "denied", reasonCode: "CAPABILITY_NOT_GRANTED" };
  return { state: "ready", legalEntityId: value.legalEntityId, source: "default", defaultRevision: value.sourceRevision };
}
